import { State } from "..";

import { AbstractStore } from ".";

import {
  NoiseSuppresionState,
  VoiceGateMode,
  VoiceInputMode,
  clampThresholdDb,
  cleanVoiceProcessingSettings,
  defaultVoiceProcessingSettings,
} from "./voiceSettingsMigration";

/**
 * Possible noise suppresion states. Browser is browser noise suppresion,
 * enhanced is RNNoise and studio is DeepFilterNet3.
 */
export type {
  NoiseSuppresionState,
  VoiceGateMode,
  VoiceInputMode,
} from "./voiceSettingsMigration";

export interface TypeVoice {
  preferredAudioInputDevice?: string;
  preferredAudioOutputDevice?: string;

  echoCancellation: boolean;
  noiseSupression: NoiseSuppresionState;
  noiseSuppressionLevel: number;

  /** stored settings migration marker, see voiceSettingsMigration.ts */
  processingVersion: number;
  gateMode: VoiceGateMode;
  gateThresholdDb: number;
  inputMode: VoiceInputMode;
  /** KeyboardEvent.code of the push-to-talk key, unset until bound */
  pushToTalkKey?: string;

  inputVolume: number;
  outputVolume: number;

  entranceSoundVolume: number;
  soundboardVolume: number;

  userVolumes: Record<string, number>;
  userMutes: Record<string, boolean>;
}

/**
 * Handles enabling and disabling client experiments.
 */
export class Voice extends AbstractStore<"voice", TypeVoice> {
  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "voice");
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeVoice {
    return {
      ...defaultVoiceProcessingSettings(),
      echoCancellation: true,
      noiseSuppressionLevel: 80,
      inputVolume: 1.0,
      outputVolume: 1.0,
      entranceSoundVolume: 1.0,
      soundboardVolume: 1.0,
      userVolumes: {},
      userMutes: {},
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeVoice>): TypeVoice {
    const data = this.default();

    if (typeof input.preferredAudioInputDevice === "string") {
      data.preferredAudioInputDevice = input.preferredAudioInputDevice;
    }

    if (typeof input.preferredAudioOutputDevice === "string") {
      data.preferredAudioOutputDevice = input.preferredAudioOutputDevice;
    }

    if (typeof input.echoCancellation === "boolean") {
      data.echoCancellation = input.echoCancellation;
    }

    // noise suppression mode, gate, input mode + one-time RNNoise migration
    Object.assign(
      data,
      cleanVoiceProcessingSettings(input as Record<string, unknown>),
    );

    if (
      typeof input.noiseSuppressionLevel === "number" &&
      Number.isFinite(input.noiseSuppressionLevel)
    ) {
      data.noiseSuppressionLevel = Math.max(
        0,
        Math.min(100, input.noiseSuppressionLevel),
      );
    }

    if (
      typeof input.entranceSoundVolume === "number" &&
      Number.isFinite(input.entranceSoundVolume) &&
      input.entranceSoundVolume >= 0
    ) {
      data.entranceSoundVolume = input.entranceSoundVolume;
    }

    if (
      typeof input.soundboardVolume === "number" &&
      Number.isFinite(input.soundboardVolume) &&
      input.soundboardVolume >= 0
    ) {
      data.soundboardVolume =
        input.soundboardVolume === 0.8 ? 1.0 : input.soundboardVolume;
    }

    if (typeof input.inputVolume === "number") {
      data.inputVolume = input.inputVolume;
    }

    if (typeof input.outputVolume === "number") {
      data.outputVolume = input.outputVolume;
    }

    if (typeof input.userVolumes === "object") {
      Object.entries(input.userVolumes)
        .filter(
          ([userId, volume]) =>
            typeof userId === "string" &&
            typeof volume === "number" &&
            Number.isFinite(volume),
        )
        .forEach(
          ([k, v]) => (data.userVolumes[k] = Math.max(0, Math.min(v, 3))),
        );
    }

    if (typeof input.userMutes === "object") {
      Object.entries(input.userMutes)
        .filter(
          ([userId, muted]) => typeof userId === "string" && muted === true,
        )
        .forEach(([k, v]) => (data.userMutes[k] = v));
    }

    return data;
  }

  /**
   * Set a user's volume
   * @param userId User ID
   * @param volume Volume
   */
  setUserVolume(userId: string, volume: number) {
    if (!Number.isFinite(volume)) return;
    this.set("userVolumes", userId, Math.max(0, Math.min(volume, 3)));
  }

  /**
   * Get a user's volume
   * @param userId User ID
   * @returns Volume or default
   */
  getUserVolume(userId: string): number {
    return this.get().userVolumes[userId] ?? 1.0;
  }

  /**
   * Set whether a user is muted
   * @param userId User ID
   * @param muted Whether they should be muted
   */
  setUserMuted(userId: string, muted: boolean) {
    this.set("userMutes", userId, muted);
  }

  /**
   * Get whether a user is muted
   * @param userId User ID
   * @returns Whether muted
   */
  getUserMuted(userId: string): boolean {
    return this.get().userMutes[userId] ?? false;
  }

  /**
   * Set the preferred audio input device
   */
  set preferredAudioInputDevice(value: string) {
    this.set("preferredAudioInputDevice", value);
  }

  /**
   * Set the preferred audio output device
   */
  set preferredAudioOutputDevice(value: string) {
    this.set("preferredAudioOutputDevice", value);
  }

  /**
   * Set echo cancellation
   */
  set echoCancellation(value: boolean) {
    this.set("echoCancellation", value);
  }

  /**
   * Set noise cancellation
   */
  set noiseSupression(value: NoiseSuppresionState) {
    this.set("noiseSupression", value);
  }

  /**
   * Set input volume
   */
  set inputVolume(value: number) {
    this.set("inputVolume", value);
  }

  /**
   * Set output volume
   */
  set outputVolume(value: number) {
    this.set("outputVolume", value);
  }

  /**
   * Get entrance sound volume
   */
  get entranceSoundVolume(): number {
    return this.get().entranceSoundVolume;
  }

  /**
   * Set entrance sound volume
   */
  set entranceSoundVolume(value: number) {
    this.set("entranceSoundVolume", value);
  }

  /**
   * Get soundboard volume
   */
  get soundboardVolume(): number {
    return this.get().soundboardVolume;
  }

  /**
   * Set soundboard volume
   */
  set soundboardVolume(value: number) {
    this.set("soundboardVolume", value);
  }

  /**
   * Get noise suppression level
   */
  get noiseSuppressionLevel(): number {
    return this.get().noiseSuppressionLevel;
  }

  /**
   * Set noise suppression level
   */
  set noiseSuppressionLevel(value: number) {
    this.set("noiseSuppressionLevel", value);
  }

  /**
   * Get the preferred audio input device
   */
  get preferredAudioInputDevice(): string | undefined {
    return this.get().preferredAudioInputDevice;
  }

  /**
   * Get the preferred audio output device
   */
  get preferredAudioOutputDevice(): string | undefined {
    return this.get().preferredAudioOutputDevice;
  }

  /**
   * Get echo cancellation
   */
  get echoCancellation(): boolean {
    return this.get().echoCancellation;
  }

  /**
   * Get noise supression
   */
  get noiseSupression(): NoiseSuppresionState {
    return this.get().noiseSupression;
  }

  /**
   * Get input volume
   */
  get inputVolume(): number {
    return this.get().inputVolume;
  }

  /**
   * Get noise supression
   */
  get outputVolume(): number {
    return this.get().outputVolume;
  }

  /**
   * Voice activity gate mode
   */
  get gateMode(): VoiceGateMode {
    return this.get().gateMode;
  }

  set gateMode(value: VoiceGateMode) {
    this.set("gateMode", value);
  }

  /**
   * Manual gate threshold in dBFS
   */
  get gateThresholdDb(): number {
    return this.get().gateThresholdDb;
  }

  set gateThresholdDb(value: number) {
    if (!Number.isFinite(value)) return;
    this.set("gateThresholdDb", clampThresholdDb(value));
  }

  /**
   * Input mode (voice activity or push-to-talk)
   */
  get inputMode(): VoiceInputMode {
    return this.get().inputMode;
  }

  set inputMode(value: VoiceInputMode) {
    this.set("inputMode", value);
  }

  /**
   * Push-to-talk key (KeyboardEvent.code)
   */
  get pushToTalkKey(): string | undefined {
    return this.get().pushToTalkKey;
  }

  set pushToTalkKey(value: string | undefined) {
    this.set("pushToTalkKey", value);
  }
}
